import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { t } from "@/i18n-js/instance";
import type { CheckoutProps } from "@/react/components/Paywalls/Checkout/types/CheckoutProps";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import { ErrorTemplate } from "@circle-react/components/shared/ErrorPages/ErrorTemplate";

interface CheckoutErrorBoundaryProps {
  checkoutProps: CheckoutProps;
  children?: ReactNode;
}

interface CheckoutErrorBoundaryState {
  hasError: boolean;
}

export class CheckoutErrorBoundary extends Component<
  CheckoutErrorBoundaryProps,
  CheckoutErrorBoundaryState
> {
  constructor(props: CheckoutErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (window.DISABLE_JS_ERROR_BOUNDARIES === "true") {
      throw error;
    }

    this.setState({ hasError: true });

    console.error("CheckoutErrorBoundary caught error:", error, errorInfo);

    const { checkoutProps } = this.props;
    const metadata = {
      checkout: {
        community_id: checkoutProps.currentCommunity?.id,
        user_id: checkoutProps.currentUser?.id,
        paywall_id: checkoutProps.paywall?.id,
        community_member_id: checkoutProps.currentCommunityMember?.id,
        url: window.location.href,
      },
    };
    notifyBugsnag(error, metadata);
  }

  override render(): ReactNode {
    const { children } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return (
        <ErrorTemplate
          errorCode={500}
          title={t("paywall_checkout.error_boundary.title")}
        />
      );
    }

    return children;
  }
}
